import {useState} from "react";
import {useSelector} from "react-redux";
import styled from 'styled-components/macro';
import classNames from 'classnames';
import Skeleton from "react-loading-skeleton";
import {useActions, useLoggedIn, useModal, useRelativeTime, useRelativeTimeFormatter, useSettings} from "../store";
import {useFeedback} from "../store/feedback";
import {useHidePost, usePostVote} from "../api/post";
import {useBlock} from "../api/user";
import {useBaseCommunity, usePostLink, useUsername} from "../store/hooks";
import {useNSFW} from "../api/nsfw";
import {useDelete} from "../api/delete";
import {selectPostVoteState} from "../store/selectors";
import {NO_VOTE} from "../utils/votes";
import {canDownload, getDownload} from "../utils/download";
import NavLink from "./nav_link";
import ReportItem from "./report_item";
import SaveItem from "./save_item";
import SharePost from "./share_post";
import FlairPost from "./flair_post";
import {PostVote} from "./vote";
import {EditPost} from "./edit";
import ModActions from "./mod_actions";
import {ModTools, Reports} from "./mod_tools";
import RoleIndicator from "./role_indicator";
import {DivButton, TextButton} from "./button";
import ResizableImage from "./resizable_image";
import TextContent from "./text_content";
import Tooltip from "./tooltip";
import ConfirmButton from "./confirm_button";
// eslint-disable-next-line import/no-cycle
import PostEmbed from "./post_embed";
import CrossPost from "./crosspost";
import Signup from "./signup";
import Login from "./login";
import useWindowDimensions, {useDimensions, useVideoDimensions} from "../utils/dimensions";
import LinkExcerpt from "./link_excerpt";
import GiveAward from "./subscriptions/give_award";
import TweetEmbed from "./tweet_embed";
import ErrorBoundary from "./error_boundary";
import VideoPlayer from "./video_player";
import PlaceholderLinkExcerpt from "./placeholder_link_excerpt";
import {showDomain} from "../utils/domains";
import {themecolors} from "../utils/themecolors";
import {
    Award as LucideAward,
    Camera,
    Check,
    Ellipsis,
    Eraser,
    Hand,
    Link,
    Lock,
    LucideExpand, Maximize2,
    OctagonAlert,
    Pin,
    Repeat2,
    SquareArrowOutUpRight,
    Twitter,
    Video,
} from "lucide-react";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCamera, faExternalLinkAlt, faLink, faQuoteLeft, faRetweet, faVideo} from '@fortawesome/free-solid-svg-icons';
import {faTwitter} from '@fortawesome/free-brands-svg-icons';

const Div = styled.div``;
const Info = styled.div``;
const Actions = styled.div``;
const Title = styled.div``;
const Preview = styled(DivButton)``;
const Score = styled.div``;
const Details = styled.div``;
const CommentCount = styled.div``;
const Expand = styled(DivButton)``;
const Posted = styled.span``;
const Content = styled.div``;
const Main = styled.div``;
const Stickied = styled.span``;
const Edited = styled.span``;
const Domain = styled.span``;
const Approved = styled.span``;
const Primary = styled.div``;
const PostFlair = styled.span``;
const UserFlair = styled.span``;
const NSFW = styled.span``;
const NewUser = styled.span``;
const Locked = styled.span``;
const VideoContainerOuter = styled.div``;
const VideoContainerInner = styled.div``;
const Award = styled.div``;
const PreviewOverlay = styled.div``;

function Post({className, post, initialExpand, show_community, inPost, active_sort}) {
    const baseCommunity = useBaseCommunity();
    const actions = useActions();
    const modal = useModal();
    const username = useUsername();
    const {show_nsfw, new_tab, beta} = useSettings();
    const [expand, setExpand] = useState(initialExpand);
    const [edit, setEdit] = useState(false);
    const date = new Date(active_sort ? post?.last_comment_created : post?.created);
    const ago = useRelativeTime(active_sort ? post?.last_comment_created : post?.created, true);
    const [reports, setReports] = useState(false);
    const loggedIn = useLoggedIn();
    const formatRelativeTime = useRelativeTimeFormatter();
    const {feedbackCh} = useFeedback();
    const vote = usePostVote(feedbackCh);
    const voteValue = useSelector(selectPostVoteState(post?.id));
    const hidePost = useHidePost(feedbackCh);
    const block = useBlock(feedbackCh);
    const nsfw = useNSFW(feedbackCh);
    const _delete = useDelete(feedbackCh);
    const [voteDir, setVoteDir] = useState(NO_VOTE);
    const handleLogin = async () => {
        modal.open(Login);
    };
    const handleBlock = async () => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            try {
                await block(post.author);
            } catch {
            }
        }
    };
    const handleHide = async () => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            try {
                await hidePost(post);
            } catch {
            }
        }
    };
    const handleReport = async () => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            try {
                modal.open(ReportItem, {post});
            } catch {
            }
        }
    };
    const handleSave = async () => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            try {
                modal.open(SaveItem, {post});
            } catch {
            }
        }
    };
    const handleShare = async () => {
        try {
            modal.open(SharePost, {post});
        } catch {
        }
    };
    const handleCrosspost = async () => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            try {
                modal.open(CrossPost, {originalPost: post});
            } catch {
            }
        }
    };
    const handleDownload = async () => {
        try {
            window.location.href = `https://dl.scored.co/api/watermark?id=${getDownload(post?.link)}&username=${post?.author}&community=${post?.community}`;
        } catch {
        }
    };
    const handleFlair = async () => {
        try {
            modal.open(FlairPost, {post});
        } catch {
        }
    };
    const handleNSFW = async () => {
        const action = actions.postNsfw(post.id, !post.is_nsfw);
        try {
            await nsfw(post.id, !post.is_nsfw);
        } catch (ex) {
            action.undo?.();
        }
    };
    const handleDelete = async () => {
        modal.close();
        const action = actions.postDeleted(post.id);
        try {
            await _delete(post.id, 'post');
        } catch {
            action.undo?.();
        }
    };
    const handleVote = async (dir) => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            if (dir !== voteValue) {
                setVoteDir(dir);
            }
            //try {
                await vote(post, dir);
            //} catch {
            //    setVoteDir(NO_VOTE);
            //}
        }
    };
    const handleEdit = () => {
        setEdit(!edit);
    };
    const handleAward = async () => {
        if (!loggedIn) {
            modal.open(Signup);
        } else {
            modal.open(GiveAward, {id: post.id, type: 'post'});
        }
    }

    const isVideo = post?.is_video || post?.link_metadata?.rumble_embed;
    const isVidey = post?.is_video && !post?.is_video_mp4 && post.video_link.startsWith("https://cdn.videy.co/");
    const iframeLink = post && !isVidey && ((post.is_video && !post.is_video_mp4 && post.video_link) || post.link_metadata?.rumble_embed);
    const isYouTube = iframeLink && (iframeLink.startsWith("https://www.youtube.com/") || iframeLink.startsWith("https://youtube.com/"));

    const isMetadata = (!post?.content && !isVideo && !post?.is_image && !post?.is_twitter && !post?.is_crosspost) && (post && post?.link_metadata?.title);
    const isPlaceholderLink = post && !post?.content && !isVideo && !post?.is_image && !post?.is_twitter && !post?.is_crosspost && !isMetadata;
    const link = usePostLink(post);
    const handleExpand = () => {
        if (post.link && !(post.is_twitter || isVideo || post.is_image || isMetadata || isPlaceholderLink)) {
            // window.location.href = post.link;
        } else {
            setExpand(expand === 'card' ? 'condensed' : 'card');
        }
    };

    const windowWidth = useWindowDimensions().width;
    const videoDimensions = useVideoDimensions(post?.media, windowWidth);

    const ExternalLinkWrapper = (post?.link && !(post.is_twitter || isVideo || post.is_image || isMetadata || isPlaceholderLink)) ? NavLink : Div;

    const compressedMedia = post?.media.resized_resolutions[0] || false;
    const dimensions = useDimensions(windowWidth, compressedMedia?.width || 0, compressedMedia?.height || 0);
    const contentType = compressedMedia?.["content-type"];

    const shouldShowDomain = post && showDomain(post.domain);

    return (
        <div
            className={classNames(
                className,
                'post',
                {
                    stickied: post?.is_stickied,
                    removed: post?.moderation?.removed_at,
                }
            )}
        >
            <Main>
                <PostVote
                    onVote={handleVote}
                    vote={voteValue}
                    score={post?.score}
                    up={post?.score_up}
                    down={post?.score_down}
                    animate={voteDir}
                />
                <ExternalLinkWrapper className={"preview-parent"} href={post?.link}
                                     target={new_tab ? '_blank' : undefined} onClick={handleExpand}>
                    <Preview style={{minHeight: (post && (post.is_deleted || !post.preview) ? "66px" : 0)}}
                        className={classNames(`${post ? 'thumb' : ''}`, {'default-thumbnail': post && (post?.is_deleted || !post?.preview)}, {blur: !show_nsfw && post?.is_nsfw})}
                    >
                        {post && !post.is_deleted && <img src={post.preview} alt=""/>}
                        {!post && <Skeleton height={66} width={66}/>}
                   {/*     {post && !post.is_deleted && post.preview && post.type === 'link' &&
                        (isVideo || post.is_twitter || post.is_image || !(post.is_twitter || isVideo || post.is_image || post.is_crosspost)) &&
                            <PreviewOverlay>
                                {post?.type === 'link' && isVideo && <Video size={14} color={'white'} />}
                                {post?.type === 'link' && post.is_twitter && <Twitter size={14} color={'white'} />}
                                {post?.type === 'link' && post.is_image && <Camera size={14} color={'white'} />}
                                {post?.type === 'link' && !(post.is_twitter || isVideo || post.is_image || post.is_crosspost) && (
                                    isMetadata ?
                                        <Link size={14} color={'white'} />
                                        :
                                        <ExternalLink size={14} color={'white'} />)
                                }
                            </PreviewOverlay>
                        }*/}
                    </Preview>
                </ExternalLinkWrapper>
                <Info>
                    <Primary>
                        <Title className={'title'}>
                            {!post && <Skeleton/>}
                            {post && (!inPost || !post.link) && (
                                <NavLink href={link} react_route target={new_tab ? '_blank' : undefined}>
                                    {post.is_deleted ? '[deleted]' : post.title}
                                </NavLink>
                            )}
                            {(inPost) && post?.link && (
                                <a href={post.link} referrerPolicy='no-referrer'
                                   target={new_tab ? '_blank' : undefined}>{post.title}</a>
                            )}
                        </Title>
                        {post?.is_nsfw &&
                        <Tooltip content={`This content is marked as Not Safe for Work.`}>
                            <NSFW>
                                <OctagonAlert size={14} />
                            </NSFW>
                        </Tooltip>
                        }
                        {post?.domain && shouldShowDomain && <Domain className={'domain'}>({post.domain})</Domain>}
                        {post?.post_flair_class &&
                        <PostFlair className='post-flair'
                                   data-flair={post.post_flair_class}>{post.post_flair_text}</PostFlair>}
                        {post?.moderation && post.moderation.approved_at !== 0 &&
                        <Tooltip
                            content={`Approved ${formatRelativeTime(post.moderation.approved_at)} ago by ${post.moderation.approved_by}`}>
                            <Approved>
                                <Check size={14} />
                            </Approved>
                        </Tooltip>}
                    </Primary>
                    {post && (
                        <Details>
                            <ExternalLinkWrapper href={post?.link} target={new_tab ? '_blank' : undefined}
                                                 onClick={handleExpand}>
                                <Expand className={baseCommunity === "TheDonald" ? "pdw" : undefined}>
                                    {baseCommunity === "TheDonald" ? (
                                        <>
                                            {post?.type === 'text' && <FontAwesomeIcon icon={faQuoteLeft}/>}
                                            {post?.type === 'link' && isVideo && <FontAwesomeIcon icon={faVideo}/>}
                                            {post?.type === 'link' && post.is_image && <FontAwesomeIcon icon={faCamera}/>}
                                            {post?.type === 'link' && post.is_twitter && <FontAwesomeIcon icon={faTwitter}/>}
                                            {post?.type === 'link' && post.is_crosspost && <FontAwesomeIcon icon={faRetweet}/>}
                                            {post?.type === 'link' && !(post.is_twitter || isVideo || post.is_image || post.is_crosspost) && (
                                                <FontAwesomeIcon icon={isMetadata ? faLink : faExternalLinkAlt}/>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {post?.type === 'text' && <Maximize2 size={15} />}
                                            {post?.type === 'link' && isVideo && <Video size={15} />}
                                            {post?.type === 'link' && post.is_image && <Camera size={15} />}
                                            {post?.type === 'link' && post.is_twitter && <Twitter size={15} />}
                                            {post?.type === 'link' && post.is_crosspost && <Repeat2 size={15} />}
                                            {post?.type === 'link' && !(post.is_twitter || isVideo || post.is_image || post.is_crosspost) && (
                                                <>{isMetadata ? <Link size={15} /> : <SquareArrowOutUpRight size={15} />}</>
                                            )}
                                        </>
                                    )}
                                </Expand>
                            </ExternalLinkWrapper>
                            <span
                                className={"desktop"}>{active_sort ? 'active ' : `${post.is_crosspost ? 'cross-' : ''}posted `}</span>
                            <Tooltip content={date.toLocaleString()}>
                                <span>{`${ago} ago`}</span>
                            </Tooltip>
                            {` by `}
                            <NavLink href={`/u/${post && post.author}`} react_route>{post ? post.author : '-'}</NavLink>
                            {' '}
                            {post?.author_flair_class &&
                            <UserFlair className='user-flair' data-flair={post.author_flair_class}>
                                {post.author_flair_text}
                            </UserFlair>
                            }
                            <RoleIndicator admin={post.is_admin} mod={post.is_moderator} proTier={post.pro_tier}/>
                            {show_community && (
                                <> in <NavLink href={`/c/${post.community}`} react_route>{post.community}</NavLink></>
                            )}
                            {post?.is_new_user && (
                                <Tooltip content={`This user is either new, or hasn't posted much.`}>
                                    <NewUser>
                                        <Hand size={14} />
                                    </NewUser>
                                </Tooltip>
                            )}
                            {post?.awards > 0 &&
                            <Award>
                                <LucideAward size={16} color={"#dcba00"} style={{margin: '0 2px'}} />
                            </Award>
                            }
                            {post?.is_edited && (
                                <Edited>
                                    <Eraser size={14} />
                                </Edited>
                            )}
                            {post?.moderation && <ModTools post={post}/>}
                            {post?.is_stickied && (
                                <Stickied>
                                    <Pin size={14} color={"#ffbe00"} fill={"#ffbe00"} />
                                </Stickied>
                            )}
                            {post?.is_locked && (
                                <Locked>
                                    <Lock size={14} />
                                </Locked>
                            )}
                        </Details>
                    )}
                    {!post && <Skeleton height={14}/>}
                    {post ? (
                        <Actions>
                            <CommentCount>
                                <NavLink href={link} target={new_tab ? '_blank' : undefined} react_route>
                                    {`${post ? post.comments : '-'} comments`}
                                </NavLink>
                            </CommentCount>
                            <TextButton onClick={handleAward}>
                                award
                            </TextButton>
                            <TextButton onClick={handleShare}>
                                share
                            </TextButton>
                            {canDownload(post?.link) &&
                            <TextButton onClick={handleDownload}>
                                download
                            </TextButton>
                            }
                            {loggedIn &&
                            <TextButton onClick={handleCrosspost}>
                                crosspost
                            </TextButton>
                            }
                            {loggedIn &&
                            <TextButton className={classNames('action', {'guest': !loggedIn})} onClick={handleSave}>
                                save
                            </TextButton>
                            }
                            {/*    {loggedIn &&
                            <ConfirmButton className={classNames('action', {'guest': !loggedIn})} onClick={handleHide}>
                                hide
                            </ConfirmButton>
                            }*/}
                            <TextButton className={classNames('action-report', 'action', {'guest': !loggedIn})} onClick={handleReport}>
                                report
                            </TextButton>
                            {post?.author !== username &&
                            <ConfirmButton className={classNames('action', {'guest': !loggedIn})} onClick={handleBlock}>
                                block
                            </ConfirmButton>
                            }
                            <TextButton className={classNames('ellipses', {guest: !loggedIn})} onClick={handleLogin}>
                                <Ellipsis size={14} />
                            </TextButton>
                            {post && username === post?.author && (
                                <>
                                    {post?.type === 'text' && (
                                        <TextButton onClick={handleEdit}>
                                            edit
                                        </TextButton>
                                    )}
                                    <ConfirmButton onClick={handleDelete}>
                                        delete
                                    </ConfirmButton>
                                    <ConfirmButton onClick={handleNSFW}>
                                        nsfw
                                    </ConfirmButton>
                                </>
                            )}
                            {post && (username === post.author || post.moderation) && (
                                <TextButton onClick={handleFlair}>
                                    flair
                                </TextButton>
                            )}
                            {post && post.moderation && (
                                <ModActions post={post} setReports={setReports} reports={reports}/>
                            )}
                        </Actions>
                    ) : <Skeleton height={14}/>}
                    {reports && <Reports id={post?.id} type={'post'}/>}
                </Info>
            </Main>
            {expand === 'card' && !post?.is_deleted && (
                <Content>
                    {post?.is_image && post.link && (!compressedMedia || (compressedMedia && contentType !== 'mp4')) && (
                        <ResizableImage post={post} alt=""/>
                    )}
                    {post?.is_image && post.link && compressedMedia && contentType === 'mp4' &&
                    <video style={{height: dimensions[1], width: dimensions[0], borderRadius: '12px'}} autoPlay loop muted playsInline
                           src={`https://media.scored.co${compressedMedia.reference}`}/>
                    }
                    {post?.type === 'text' && (
                        <TextContent removed={post.is_removed} deleted={post.is_deleted} content={post.content}
                                     type={"Post"} removalSource={post?.removal_source}/>
                    )}
                    {iframeLink &&
                    <VideoContainerOuter>
                        <VideoContainerInner>
                            <iframe src={iframeLink} frameBorder="0" allowFullScreen="" allow="fullscreen;"
                                    title={post.title} referrerPolicy={isYouTube ? "strict-origin-when-cross-origin" : "no-referrer"}/>
                        </VideoContainerInner>
                    </VideoContainerOuter>
                    }
                    {isVideo && post?.is_video_mp4 && (
                        <video controls style={{maxHeight: '75vh', borderRadius: '12px'}}>
                            <source src={`${post.video_link}#t=0.001`} type="video/mp4" referrerPolicy="no-referrer" playsInline/>
                            Your browser does not support the video tag.
                        </video>
                    )}
                    {isVideo && isVidey && (
                        <VideoPlayer src={`${post.video_link}`} inPost={inPost}/>
                    )}
                    {/*   {isVideo && isVidey && (
                        <video controls style={{height: videoDimensions[0], maxWidth: videoDimensions[1], maxHeight: '75vh'}}>
                            <source src={`${post.video_link}#t=0.001`} type="video/mp4" referrerPolicy="no-referrer"/>
                            Your browser does not support the video tag.
                        </video>
                        )}*/}
                    {post?.is_twitter && post.link_metadata?.twitter && (
                        <ErrorBoundary>
                            <TweetEmbed data={post.link_metadata.twitter} className={"tweet"}/>
                        </ErrorBoundary>
                    )}
                    {post?.is_crosspost &&
                        <PostEmbed uuid={post.crosspost_uuid}/>
                    }
                    {isMetadata &&
                        <a href={post.link} referrerPolicy='no-referrer' target={"_blank"}>
                            <LinkExcerpt data={post.link_metadata} domain={post.domain}/>
                        </a>
                    }
                    {isPlaceholderLink &&
                        <a href={post.link} referrerPolicy='no-referrer' target={"_blank"}>
                            <PlaceholderLinkExcerpt post={post} />
                        </a>
                    }
                </Content>
            )}
            {edit && <EditPost post={post} onClose={() => setEdit(false)}/>}
        </div>
    );
}

export default styled(Post)`
  padding: 8px 12px 8px 0;

  ${({post}) => !post && `
      ${Title} {
          width: 100%;
          margin-bottom: 2px;
      }
  `}
  ${Main} {
    display: flex;
  }

  ${Actions} {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    font-size: 12px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }

  ${Actions} > * {
    margin-right: 8px;
  }
  
  ${Info} {
    line-height: 18px;
    flex-direction: column;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  ${Approved} {
    color: ${({theme}) => theme.placeholder_green_1};
    margin: 0 4px;
    font-size: 13px;
  }

  ${Preview} {
    position: relative;
    width: 66px;
    min-width: 66px;
    max-height: 66px;
    margin-right: 12px;
    border-radius: 4px;
    overflow: hidden;
  }
  
  ${PreviewOverlay} {
    position: absolute;
    background-color: rgba(0, 0, 0, 22%);
    width: 22px;
    height: 16px;
    border-radius: 4px;
    bottom: 4px;
    left: 4px;
    font-size: 10px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({theme}) => themecolors.dark_high_impact_text};
  }
  
  .default-thumbnail {
    background-color: ${({theme}) => theme.skeleton.color};
  }

  ${Preview} img {
    border-radius: 4px;
    height: 66px;
    width: 66px;
    object-fit: cover;
    object-position: center;
  }

  ${Preview} img:not([src]),
  ${Preview} img[src=""] {
    visibility: hidden;
  }

  ${Score} {
    margin-top: 2px;
    margin-bottom: 2px;
  }

  ${Details} {
    display: flex;
    font-size: 13px;
    margin-top: 2px;
    margin-bottom: 2px;
    align-items: center;
    width: 100%;
    flex-direction: row;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : 'inherit'};
    white-space: break-spaces;
    flex-wrap: wrap;
  }

  ${Primary} {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
  }

  ${Title} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : theme.placeholder_link_1};
    font-size: 16px;
    display: inline-block;
    -webkit-font-smoothing: auto;
  }

  ${Title} a:visited {
    color: ${({theme}) => theme.color.name === 'dark' ? theme.placeholder_gray_2 : themecolors.light_high_impact_text};
  }
  
  ${Details} a,
  ${CommentCount} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : theme.placeholder_link_2};
    font-weight: ${({theme}) => theme.color.name === 'dark' ? '500' : 'inherit'};
  }

  ${CommentCount} {
    font-weight: 500;
  }

  ${Expand} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_low_impact_text : 'grey'};
    font-size: 14px;
    margin-right: 6px;
    cursor: pointer;
    display: flex;
  }

  ${Expand}.pdw {
    color: ${({theme}) => theme.placeholder_gray_2};
  }

  ${Content} {
    margin: 8px 12px 0 136px;
  }

  ${Content} img {
    max-width: calc(min(680px,100%));
  }

  ${TextContent} {
    font-size: 14px;
  }

  ${EditPost} {
    margin-left: 126px;
  }

  ${Posted} {
    display: flex;
  }

  ${Stickied}, ${Locked} {
    font-size: 12px;
    margin-left: 6px;
  }

  ${Edited}, ${NewUser} {
    margin-left: 6px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_low_impact_text};
  }

  ${Domain} {
    margin-left: 4px;
    font-size: 12px;
    color: ${({theme}) => theme.placeholder_gray_2};
  }

  ${PostFlair} {
    position: relative;
    display: inline-block;
    margin: 2px 4px 0 4px;
    padding: 2px 10px;
    white-space: nowrap;
    border-radius: 4px;
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.dark_field : themecolors.light_input};
    font-size: 14px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};
  }

  ${NSFW} {
    margin-left: 6px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_low_impact_text};
  }

  ${VideoContainerOuter} {
    max-width: 560px;
  }
  ${VideoContainerInner} {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    max-width: 560px;
    border-radius: 8px;
  }

  ${VideoContainerInner} iframe,
  ${VideoContainerInner} object,
  ${VideoContainerInner} embed { 
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  
  &.removed {
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.pink_2 : theme.pink_1};
  }

  &.stickied ${Title} a {
    font-weight: 500;
  }

  &.stickied ${Title}:before {
    margin-right: 4px;
  }

  &.stickied ${Title}:after {
    margin-left: 4px;
  }

  &.stickied ${Domain} {
    font-weight: 500;
  }

  .theme-dark &.post {
    background: ${({theme}) => themecolors.dark_background_2};
  }

  .theme-dark &.post.stickied {
    border-bottom: none;
  }

  & .ellipses {
    display: none;
  }

  & .blur img {
    filter: blur(3px);
  }

  @media only screen and (max-width: 780px) {
    ${Content} {
      margin: 8px 0 0 12px;
    }

    ${Content} img {
      max-width: 100%;
    }

    ${EditPost} {
      margin-left: 0;
      padding-left: 56px;
    }

    .preview-parent {
      order: 2;
    }
    
    ${Preview} {
      margin-left: 8px;
      margin-right: 4px;
    }

    .default-thumbnail {
      display: none;
    }

    & .desktop {
      display: none;
    }

    & .ellipses.guest {
      display: block;
    }

    & .action.guest {
      display: none;
    }
  }
`;
